import { useEffect, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import { EXP_AP_46_TRUSTPILOT_CSAT } from 'modules/common/constants/experiments';
import useStatsigExperiment from 'modules/common/hooks/useStatsigExperiment';
import { useMember } from 'modules/common/queries/member';

interface UseTrustPilotProps {
    featureName: string;
    featureSection: string;
}

export function useTrustPilot({ featureName, featureSection }: UseTrustPilotProps) {
    const {
        client: { getDynamicConfig },
    } = useStatsigClient();

    const [shouldLoadTrustPilot, setShouldLoadTrustPilot] = useState(false);
    const [shouldShowTrustPilot, setShouldShowTrustPilot] = useState(false);
    const [shouldShowTrustPilotPrompt, setShouldShowTrustPilotPrompt] = useState(false);
    const [isTrustPilotPromptVisible, setIsTrustPilotPromptVisible] = useState(false);
    const [isTrustPilotQualified, setIsTrustPilotQualified] = useState(false);

    const { data: member } = useMember();

    const { variation: CSATExperimentGroup } = useStatsigExperiment(EXP_AP_46_TRUSTPILOT_CSAT);
    const showTrustpilotPrompt = CSATExperimentGroup === 'test';

    // Get the trustpilot_allowed_surveys config from Statsig
    // The config format should be: { "dashboard": ["qte", "payroll", "accounting", "invoicing"] }
    const trustpilotConfig = getDynamicConfig('trustpilot_allowed_surveys');

    // Check if TrustPilot should be shown for this survey
    const shouldShowTrustPilotForSurvey = (score: number): boolean => {
        // Check if score >= 4 and member hasn't seen TrustPilot survey
        if (score < 4 || member?.trustpilot_survey_completed_at) {
            return false;
        }

        const allowedSurveyTypes = trustpilotConfig.get(featureSection.toLocaleLowerCase(), []);

        const isSurveyAllowed =
            Array.isArray(allowedSurveyTypes) && allowedSurveyTypes.includes(featureName.toLocaleLowerCase());

        return !!isSurveyAllowed;
    };

    const handleScoreChanged = (submittedScore: number) => {
        const qualifies = shouldShowTrustPilotForSurvey(submittedScore);
        setIsTrustPilotQualified(qualifies);

        if (showTrustpilotPrompt) {
            setShouldLoadTrustPilot(false);
        } else {
            setShouldLoadTrustPilot(qualifies);
        }
    };

    const handleScoreSubmitted = (submittedScore: number) => {
        const qualifies = shouldShowTrustPilotForSurvey(submittedScore);
        setIsTrustPilotQualified(qualifies);

        if (showTrustpilotPrompt) {
            setShouldShowTrustPilotPrompt(qualifies);
        } else {
            setShouldShowTrustPilot(qualifies);
        }
    };

    const handleTrustPilotPromptContinue = () => {
        if (!isTrustPilotQualified) {
            return;
        }
        setShouldLoadTrustPilot(true);
        setShouldShowTrustPilot(true);
        setShouldShowTrustPilotPrompt(false);
    };

    useEffect(() => {
        if (shouldShowTrustPilotPrompt) {
            setIsTrustPilotPromptVisible(true);
        } else {
            setIsTrustPilotPromptVisible(false);
        }
    }, [shouldShowTrustPilotPrompt]);

    useEffect(() => {
        if (!member?.id || !CSATExperimentGroup) {
            return;
        }
        collectiveTracking.sendEvent({
            eventType: 'identify',
            properties: {
                member_id: member?.id,
                CSAT_Trustpilot_Experiment_Group: CSATExperimentGroup,
            },
        });
    }, [member?.id, CSATExperimentGroup]);

    return {
        shouldLoadTrustPilot,
        shouldShowTrustPilot,
        shouldShowTrustPilotPrompt,
        isTrustPilotPromptVisible,
        setIsTrustPilotPromptVisible,
        isTrustPilotQualified,
        showTrustpilotPrompt,
        handleScoreChanged,
        handleScoreSubmitted,
        handleTrustPilotPromptContinue,
    };
}
